<template>
  <div class="card-container">
    <div class="label-row">
      <div class="label">
        Add a monetary reward (optional)
        <small>Attach a monetary reward to make this award even more special.</small>
      </div>
      <b-form-checkbox :checked="enabled" switch @change="$emit('update:enabled', $event)" />
    </div>

    <template v-if="enabled">
      <slot name="reward-amounts">
        <div class="reward-amount-container">
          <div class="label">
            Reward amount options
            <small>
              Choose one or more values that can be awarded with this prize. If you select multiple, the person
              suggesting a reward can choose the amount.
            </small>
          </div>
          <create-reward-amounts-modal
            :value="amounts"
            :default-minor="defaultAmountMinor"
            :currency-symbol="currencySymbol"
            @input="$emit('update:amounts', $event)"
            @update:defaultMinor="$emit('update:defaultAmountMinor', $event)"
          />
          <small v-if="amountError" class="text-danger">{{ amountError }}</small>
        </div>
      </slot>

      <slot name="after-amounts" />

      <hr />

      <div class="checkbox-list-container">
        <div class="label">How can recipients redeem their prize?</div>
        <div class="checkbox-list" rules="required">
          <b-form-checkbox
            v-for="opt in redemptionOptionChoices"
            :key="opt.value"
            :checked="redemptionOptions.includes(opt.value)"
            @input="checked => onRedemptionChange(opt.value, checked)"
          >
            {{ opt.text }}
          </b-form-checkbox>
        </div>
        <small v-if="redemptionError" class="text-danger">{{ redemptionError }}</small>
      </div>

      <hr />

      <app-input
        :value="expirationMonths"
        :validate-immediate="false"
        name="Expiration months"
        type="number"
        placeholder="e.g. 12"
        append="mo"
        inputmode="numeric"
        pattern="[0-9]*"
        step="1"
        :rules="enabled ? 'required|min_value:1|max_value:120' : ''"
        class="expiration-months-input"
        @input="onExpirationInput"
      >
        <template #label>
          <div class="label">
            Expiration
            <small>
              How many months will the monetary reward be available? (expired rewards are returned to your reward
              balance)
            </small>
          </div>
        </template>
      </app-input>
    </template>
  </div>
</template>

<script setup>
import AppInput from '@/components/AppInput.vue';
import CreateRewardAmountsModal from '@/pages/recognition/components/CreateRewardAmountsModal.vue';
import { RECOGNITION_REDEMPTION_METHOD_CHOICES } from '@/pages/recognition/constants';

const props = defineProps({
  enabled: {
    type: Boolean,
    default: false,
  },
  currencySymbol: {
    type: String,
    default: '£',
  },
  amounts: {
    type: [Number, Array],
    default: undefined,
  },
  defaultAmountMinor: {
    type: Number,
    default: undefined,
  },
  amountError: {
    type: String,
    default: '',
  },
  redemptionOptions: {
    type: Array,
    default: () => [],
  },
  redemptionError: {
    type: String,
    default: '',
  },
  expirationMonths: {
    type: [String, Number],
    default: '',
  },
});

const emit = defineEmits([
  'update:enabled',
  'update:amounts',
  'update:defaultAmountMinor',
  'update:redemptionOptions',
  'update:expirationMonths',
]);

const redemptionOptionChoices = RECOGNITION_REDEMPTION_METHOD_CHOICES;

const onRedemptionChange = (value, checked) => {
  const current = [...(props.redemptionOptions || [])];
  if (checked && !current.includes(value)) {
    current.push(value);
  } else if (!checked) {
    const idx = current.indexOf(value);
    if (idx > -1) {
      current.splice(idx, 1);
    }
  }
  emit('update:redemptionOptions', current);
};

const onExpirationInput = value => {
  const cleaned = String(value ?? '').replace(/[^0-9]/g, '');
  emit('update:expirationMonths', cleaned);
};
</script>

<style scoped lang="scss">
@import '@/pages/recognition/styles/award-form';
</style>
